/**
 * BatchUploadList Component
 *
 * Displays list of files selected for upload with progress indicators.
 */

import { FileText, X, CheckCircle, AlertCircle, Loader2, RefreshCw } from 'lucide-react';
import type { UploadFile } from '../../types';

interface BatchUploadListProps {
  files: UploadFile[];
  onRemove: (fileId: string) => void;
  onRetry?: (fileId: string) => void;
  uploadProgress?: Record<string, number>;
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
}

function FileStatusIcon({ status }: { status: UploadFile['status'] }) {
  switch (status) {
    case 'uploading':
      return <Loader2 size={16} className="text-primary-600 dark:text-primary-400 animate-spin" />;
    case 'success':
      return <CheckCircle size={16} className="text-green-600 dark:text-green-400" />;
    case 'error':
      return <AlertCircle size={16} className="text-red-600 dark:text-red-400" />;
    default:
      return <FileText size={16} className="text-gray-500 dark:text-gray-400" />;
  }
}

function FileStatusText({ file }: { file: UploadFile }) {
  switch (file.status) {
    case 'uploading':
      return (
        <span className="text-primary-600 dark:text-primary-400">
          Uploading... {file.progress > 0 ? `${file.progress}%` : ''}
        </span>
      );
    case 'success':
      return <span className="text-green-600 dark:text-green-400">Uploaded</span>;
    case 'error':
      return <span className="text-red-600 dark:text-red-400">{file.error || 'Upload failed'}</span>;
    default:
      return <span className="text-gray-500 dark:text-gray-400">Ready to upload</span>;
  }
}

export function BatchUploadList({ files, onRemove, onRetry, uploadProgress = {} }: BatchUploadListProps) {
  if (files.length === 0) {
    return null;
  }

  const pendingCount = files.filter(f => f.status === 'pending').length;
  const uploadingCount = files.filter(f => f.status === 'uploading').length;
  const successCount = files.filter(f => f.status === 'success').length;
  const errorCount = files.filter(f => f.status === 'error').length;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 bg-gray-50 dark:bg-gray-900/40 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {files.length} file{files.length !== 1 ? 's' : ''}
            </span>
            <div className="flex items-center gap-3 text-xs">
              {pendingCount > 0 && (
                <span className="text-gray-500 dark:text-gray-400">{pendingCount} pending</span>
              )}
              {uploadingCount > 0 && (
                <span className="text-primary-600 dark:text-primary-400">{uploadingCount} uploading</span>
              )}
              {successCount > 0 && (
                <span className="text-green-600 dark:text-green-400">{successCount} uploaded</span>
              )}
              {errorCount > 0 && (
                <span className="text-red-600 dark:text-red-400">{errorCount} failed</span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* File list */}
      <div className="max-h-64 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
        {files.map(file => {
          const progress = uploadProgress[file.id] ?? file.progress;
          const canRemove = file.status === 'pending' || file.status === 'error';

          return (
            <div
              key={file.id}
              className={`px-4 py-3 ${file.status === 'error' ? 'bg-red-50 dark:bg-red-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-700'}`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <FileStatusIcon status={file.status} />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{file.file.name}</p>
                    <div className="flex items-center gap-2 text-xs">
                      <span className="text-gray-500 dark:text-gray-400">{formatFileSize(file.file.size)}</span>
                      <span className="text-gray-300 dark:text-gray-600">|</span>
                      <FileStatusText file={file} />
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {file.status === 'error' && onRetry && (
                    <button
                      onClick={() => onRetry(file.id)}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/20 hover:bg-primary-100 dark:hover:bg-primary-900/30 rounded transition-colors"
                      title="Retry upload"
                      aria-label={`Retry uploading ${file.file.name}`}
                    >
                      <RefreshCw size={12} />
                      Retry
                    </button>
                  )}
                  {canRemove && (
                    <button
                      onClick={() => onRemove(file.id)}
                      className="p-1 text-gray-400 dark:text-gray-500 hover:text-red-500 rounded transition-colors"
                      title="Remove file"
                      aria-label={`Remove ${file.file.name}`}
                    >
                      <X size={16} />
                    </button>
                  )}
                </div>
              </div>

              {/* Progress bar */}
              {file.status === 'uploading' && (
                <div className="mt-2">
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5 overflow-hidden">
                    <div
                      className="bg-primary-600 h-full rounded-full transition-all duration-300"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
